import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Form, Field, FormSubmission, toggleFormStatus, cloneForm, deleteForm, exportFormData } from '../utils/api';
import { Button, Card, CardContent, Table, TableHeader, TableRow, TableHead, TableBody, TableCell, Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui';
import { DownloadIcon, MoreHorizontalIcon, CopyIcon, CodeIcon, PublishIcon, UnpublishIcon, ExportIcon, TrashIcon } from '../components/Icons';
import { exportToCSV } from '../utils';
import { getForm, getSubmissions } from '../utils/api';

const FormResponsesPage: React.FC = () => {
    const { formId } = useParams<{ formId: string }>();
    const [form, setForm] = useState<Form | null>(null);
    const [fields, setFields] = useState<Field[]>([]);
    const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        if (!formId) return;
        setLoading(true);
        try {
            const formData = await getForm(formId);
            const submissionsData = await getSubmissions(formId);
            
            setForm(formData);
            setFields(formData.fields?.sort((a: Field, b: Field) => a.order_index - b.order_index) || []);
            setSubmissions(submissionsData);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [formId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (loading) return <div className="p-8 text-center">Loading responses...</div>;
    if (!form) return <div className="p-8 text-center">Form not found.</div>;

    return (
        <div className="container mx-auto p-4 md:p-8">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <Link to="/" className="text-sm text-muted-foreground hover:underline">&larr; Back to Forms</Link>
                    <h1 className="text-3xl font-bold">{form.name} Responses</h1>
                </div>
                <div className="flex items-center space-x-2">
                    {/* Form Actions Menu */}
                    <Dialog>
                        <DialogTrigger asChild>
                            <Button variant="outline">
                                <MoreHorizontalIcon className="h-4 w-4 mr-2" />
                                Actions
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Form Actions</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-2">
                                <Button 
                                    variant="ghost" 
                                    className="w-full justify-start"
                                    onClick={async () => {
                                        try {
                                            await toggleFormStatus(formId!);
                                            await fetchData(); // Refresh form data
                                        } catch (error) {
                                            console.error('Failed to toggle status:', error);
                                        }
                                    }}
                                >
                                    {form?.status === 'published' ? (
                                        <>
                                            <PublishIcon className="h-4 w-4 mr-2" />
                                            Make Private
                                        </>
                                    ) : (
                                        <>
                                            <UnpublishIcon className="h-4 w-4 mr-2" />
                                            Publish Form
                                        </>
                                    )}
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    className="w-full justify-start"
                                    onClick={async () => {
                                        try {
                                            const result = await cloneForm(formId!);
                                            window.location.href = `/form/${result.newFormId}/edit`;
                                        } catch (error) {
                                            console.error('Failed to clone form:', error);
                                        }
                                    }}
                                >
                                    <CopyIcon className="h-4 w-4 mr-2" />
                                    Clone Form
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    className="w-full justify-start"
                                    onClick={async () => {
                                        try {
                                            const data = await exportFormData(formId!);
                                            const jsonContent = JSON.stringify(data, null, 2);
                                            const blob = new Blob([jsonContent], { type: 'application/json' });
                                            const url = URL.createObjectURL(blob);
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.download = `${data.form.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_export.json`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            URL.revokeObjectURL(url);
                                        } catch (error) {
                                            console.error('Failed to export:', error);
                                        }
                                    }}
                                >
                                    <ExportIcon className="h-4 w-4 mr-2" />
                                    Export Data
                                </Button>
                                <div className="h-px bg-border my-2" />
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <Button variant="ghost" className="w-full justify-start text-destructive hover:text-destructive">
                                            <TrashIcon className="h-4 w-4 mr-2" />
                                            Delete Form
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Delete Form</DialogTitle>
                                        </DialogHeader>
                                        <p className="text-muted-foreground">
                                            Are you sure you want to delete this form and all its submissions? This action cannot be undone.
                                        </p>
                                        <DialogFooter>
                                            <DialogClose asChild>
                                                <Button variant="outline">Cancel</Button>
                                            </DialogClose>
                                            <Button 
                                                variant="destructive"
                                                onClick={async () => {
                                                    try {
                                                        await deleteForm(formId!);
                                                        window.location.href = '/';
                                                    } catch (error) {
                                                        console.error('Failed to delete form:', error);
                                                    }
                                                }}
                                            >
                                                Delete Form
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>
                            </div>
                        </DialogContent>
                    </Dialog>
                    <Button onClick={() => exportToCSV(submissions, fields, form.name)}>
                        <DownloadIcon className="h-4 w-4 mr-2" />
                        Export CSV
                    </Button>
                </div>
            </div>

            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Submitted At</TableHead>
                                {fields.map(field => (
                                    <TableHead key={field.id}>{field.label}</TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {submissions.length > 0 ? submissions.map(submission => (
                                <TableRow key={submission.id}>
                                    <TableCell>{new Date(submission.submitted_at).toLocaleString()}</TableCell>
                                    {fields.map(field => (
                                        <TableCell key={field.id}>
                                            {String(submission.field_responses[field.id] || 'N/A')}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={fields.length + 1} className="text-center h-24">
                                        No responses yet.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
};

export default FormResponsesPage;